import { useQueries } from "@tanstack/react-query";

import { ProjectLocationWithProject } from "../../../models/ProjectData";
import DataSvc from "../../../services/dataSvc";

const useLocationProjects = (
  organizationIds?: string[],
  locationId?: string
) => {
  const queries = useQueries({
    queries:
      organizationIds?.map((organizationId) => ({
        queryKey: ["locationProjects", organizationId, locationId],
        queryFn: async (): Promise<ProjectLocationWithProject | null> => {
          if (!organizationId || !locationId) {
            return null;
          }
          try {
            const data = await DataSvc.getLocationProjects({
              organizationId,
              locationId,
            });
            return data;
          } catch (error) {
            // Silently handle errors when project data hasn't been generated yet
            console.warn(
              `No project data for organization ${organizationId}, location ${locationId}`,
              error
            );
            return null;
          }
        },
        enabled: !!organizationId && !!locationId,
      })) ?? [],
  });

  // Filter to show banners based on the following rules:
  // 1. Must have an onboarding_link (no link = no banner)
  // 2. Status must NOT be COMPLETE (complete = no banner)
  // 3. If SCHEDULED, must have a scheduled_date to show
  // 4. If INCOMPLETE, show the banner with link
  const locationProjectsData = queries
    .map((query: any) => query.data)
    .filter(
      (
        data: ProjectLocationWithProject | null | undefined
      ): data is ProjectLocationWithProject => {
        // Basic validation
        if (!data || !data.project?.onboarding_link) {
          return false;
        }

        // Don't show if COMPLETE
        if (data.onboarding_status === "COMPLETE") {
          return false;
        }

        // For SCHEDULED, must have a scheduled_date
        if (
          data.onboarding_status === "SCHEDULED" &&
          !data.onboarding_scheduled_date
        ) {
          return false;
        }

        // Show for INCOMPLETE (with link) or SCHEDULED (with date)
        return true;
      }
    );

  return { locationProjectsData };
};

export default useLocationProjects;
